import { PersonAddAlt1 } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { useIsMutating } from "@tanstack/react-query";
import classNames from "classnames";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import CustomerDialog from "../../pages/PosPage/components/CustomerDialog/CustomerDialog";
import SmallTablePlaceholder from "../SmallTablePlaceholder";
import clear from "./clear.svg";
import { useStyles } from "./styles";

import { PosContext } from "../../shared/PosContext";
import { AppContext } from "../../AppContext";
import axios from "axios";

const AddPaymentWidget = ({
    selectedTip,
    selectedDiscount,
    onUpdateTip,
    onUpdateDiscount,
    onConfirm,
    handlePropertyChange,
    isCreatingInvoice,
    backCallback,
    isLoading,
    setPaymentType,
    presetDiscount,
    presetDiscountAmount,
    orderOnHouse,
    setOrderOnHouse,
    setSelectedCustomer,
    selectedCustomer,
    payingAll,
    order,
    invoice,
    fixedDiscountAmount,
    setFixedDiscountAmount,
    discountPercentage,
    setDiscountPercentage,
}) => {
    const classes = useStyles();

    const { refetchCustomers, refetchAddresses, refetchTablesData } =
        useContext(PosContext);
    const { posProfile } = useContext(AppContext);

    const isMutating = useIsMutating();

    const [internalMessage, setInternalMessage] = useState(null);
    const [dollarRate, setDollarRate] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [changeCustomerDialogOpen, setChangeCustomerDialogOpen] =
        useState(false);

    // Payment methods driven by POS Profile
    const paymentMethods = useMemo(() => {
        const methods =
            posProfile?.payments
                ?.map((p) => p?.mode_of_payment)
                ?.filter(Boolean) || [];
        // Fallback for safety if profile is missing/empty
        return methods.length > 0 ? methods : ["Cash", "Credit Card"];
    }, [posProfile?.payments]);

    const companyCurrency = useMemo(() => {
        return posProfile?.currency || "ILS";
    }, [posProfile?.currency]);

    const defaultPaymentMethod = useMemo(() => {
        const defaultFromProfile = posProfile?.payments?.find((p) => p?.default == 1)
            ?.mode_of_payment;
        return defaultFromProfile || paymentMethods[0];
    }, [posProfile?.payments, paymentMethods]);

    // Amounts per payment method (supports mixed payments)
    const [paymentAmounts, setPaymentAmounts] = useState({});
    const [activePaymentMethod, setActivePaymentMethod] = useState(null);
    const [currentInput, setCurrentInput] = useState("0"); // Current calculator display (for active method)
    const [selectedButton, setSelectedButton] = useState("");

    // New state for fixed discount amount
    const [discountType, setDiscountType] = useState("percentage"); // "percentage" or "fixed"

    // Effect to set fixed discount amount when preset discount amount exists
    // But only for full order payments, not partial payments
    useEffect(() => {
        if (invoice) {
            setFixedDiscountAmount(invoice.discount_amount);
            setDiscountPercentage(invoice.discount_percentage);
        }
    }, [invoice]);

    // Track which methods were set via "Order amount" click
    const [setViaOrderAmountByMethod, setSetViaOrderAmountByMethod] = useState({});

    // Currency handling per method (account currency)
    const [methodCurrencyMap, setMethodCurrencyMap] = useState({});
    const [exchangeRatesToCompany, setExchangeRatesToCompany] = useState({});

    const getCurrencySymbol = (currency) => {
        const c = (currency || "").toUpperCase();
        if (c === "ILS") return "₪";
        if (c === "USD") return "$";
        if (c === "EUR") return "€";
        if (c === "GBP") return "£";
        return c ? `${c} ` : "";
    };

    // Fetch account currency for each Mode of Payment from ERPNext (best-effort).
    useEffect(() => {
        let cancelled = false;
        const company = posProfile?.company;
        if (!company || !paymentMethods.length) return;

        const fetchCurrencyForMethod = async (modeOfPayment) => {
            try {
                const { data: mopRes } = await axios.get(
                    "method/frappe.client.get",
                    {
                        params: { doctype: "Mode of Payment", name: modeOfPayment },
                    }
                );
                const mop = mopRes?.message;
                const accounts = mop?.accounts || [];
                const row =
                    accounts.find((a) => a.company === company) || accounts[0];
                const defaultAccount = row?.default_account;
                if (!defaultAccount) return null;

                const { data: accRes } = await axios.get(
                    "method/frappe.client.get",
                    {
                        params: { doctype: "Account", name: defaultAccount },
                    }
                );
                const acc = accRes?.message;
                return acc?.account_currency || null;
            } catch (e) {
                return null;
            }
        };

        (async () => {
            const updates = {};
            for (const method of paymentMethods) {
                const cur = await fetchCurrencyForMethod(method);
                updates[method] = (cur || companyCurrency).toUpperCase();
            }
            if (!cancelled) {
                setMethodCurrencyMap((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [paymentMethods, posProfile?.company, companyCurrency]);

    // Fetch exchange rates to company currency for any non-company currencies we discover.
    useEffect(() => {
        let cancelled = false;
        const needed = new Set();
        paymentMethods.forEach((m) => {
            const c = (methodCurrencyMap[m] || companyCurrency).toUpperCase();
            if (c && c !== companyCurrency.toUpperCase()) needed.add(c);
        });

        const existing = new Set(Object.keys(exchangeRatesToCompany || {}));
        const missing = Array.from(needed).filter((c) => !existing.has(c));
        if (missing.length === 0) return;

        (async () => {
            const updates = {};
            for (const fromCurrency of missing) {
                try {
                    // Special-case USD->ILS to reuse existing rate to reduce calls.
                    if (
                        fromCurrency === "USD" &&
                        companyCurrency.toUpperCase() === "ILS" &&
                        dollarRate
                    ) {
                        updates[fromCurrency] = Number(dollarRate);
                        continue;
                    }
                    const res = await fetch(
                        `https://api.exchangerate-api.com/v4/latest/${fromCurrency}`
                    );
                    const json = await res.json();
                    const rate = json?.rates?.[companyCurrency.toUpperCase()];
                    if (rate) {
                        updates[fromCurrency] = Number(rate);
                    }
                } catch (e) {
                    // ignore
                }
            }
            if (!cancelled && Object.keys(updates).length) {
                setExchangeRatesToCompany((prev) => ({ ...prev, ...updates }));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        paymentMethods,
        methodCurrencyMap,
        companyCurrency,
        exchangeRatesToCompany,
        dollarRate,
    ]);

    const paymentAmountsInCompanyCurrency = useMemo(() => {
        const out = {};
        paymentMethods.forEach((m) => {
            const entered = Number(paymentAmounts[m]) || 0;
            const fromCurrency = (methodCurrencyMap[m] || companyCurrency).toUpperCase();
            const toCurrency = companyCurrency.toUpperCase();
            if (fromCurrency === toCurrency) {
                out[m] = entered;
                return;
            }
            const rate = exchangeRatesToCompany[fromCurrency];
            if (!rate) {
                // If we don't know the rate yet, assume 1 to avoid blocking UX
                out[m] = entered;
                return;
            }
            out[m] = Number((entered * rate).toFixed(2));
        });
        return out;
    }, [paymentAmounts, paymentMethods, methodCurrencyMap, companyCurrency, exchangeRatesToCompany]);

    const convertCompanyToMethod = (amountCompany, method) => {
        const amt = Number(amountCompany) || 0;
        const fromCurrency = (methodCurrencyMap[method] || companyCurrency).toUpperCase();
        const toCurrency = companyCurrency.toUpperCase();
        if (fromCurrency === toCurrency) return amt;
        const rate = exchangeRatesToCompany[fromCurrency];
        if (!rate) return amt;
        return Number((amt / rate).toFixed(2));
    };


    // Ensure paymentAmounts contains keys for all methods, and active method is valid
    useEffect(() => {
        setPaymentAmounts((prev) => {
            const next = { ...prev };
            paymentMethods.forEach((m) => {
                if (next[m] == null) next[m] = 0;
            });
            // Drop removed methods
            Object.keys(next).forEach((k) => {
                if (!paymentMethods.includes(k)) {
                    delete next[k];
                }
            });
            return next;
        });
        setActivePaymentMethod((prev) => {
            if (prev && paymentMethods.includes(prev)) return prev;
            return defaultPaymentMethod || paymentMethods[0] || null;
        });
        setSetViaOrderAmountByMethod((prev) => {
            const next = { ...prev };
            paymentMethods.forEach((m) => {
                if (next[m] == null) next[m] = false;
            });
            Object.keys(next).forEach((k) => {
                if (!paymentMethods.includes(k)) {
                    delete next[k];
                }
            });
            return next;
        });
    }, [paymentMethods, defaultPaymentMethod]);

    // Local guard to prevent rapid double-click submissions
    const confirmClickLockedRef = useRef(false);

    const tables = [
        {
            name: "Table 1",
            floor: "1",
            actions: [],
            canDoBack: true,
        },
    ];

    // Calculate the proper discounted amount
    const finalOrderAmount = useMemo(() => {
        if (invoice) {
            let invoiceTotal = invoice.grand_total;

            if (invoice.discount_amount > 0) {
                return invoiceTotal;
            }

            if (fixedDiscountAmount > 0) {
                return invoiceTotal - fixedDiscountAmount;
            }

            if (discountPercentage > 0) {
                return Math.ceil(invoiceTotal - (invoiceTotal * discountPercentage) / 100);
            }

            return invoiceTotal;
        }
        return 0;
    }, [invoice, fixedDiscountAmount, discountPercentage]);

    const totalPaidInCompanyCurrency = useMemo(() => {
        return paymentMethods.reduce(
            (sum, m) => sum + (Number(paymentAmountsInCompanyCurrency[m]) || 0),
            0
        );
    }, [paymentAmountsInCompanyCurrency, paymentMethods]);
    const remainingAmount = Math.max(0, finalOrderAmount - totalPaidInCompanyCurrency);
    const changeAmount = Math.max(0, totalPaidInCompanyCurrency - finalOrderAmount);

    const userCanGiveDiscount = () => {
        return true;
    };

    // Enhanced keyboard support
    const handleKeyboardInput = useCallback(
        (event) => {
            // Avoid intercepting typing when focused inside any input fields or editable areas
            const target = event.target;
            const tagName = target?.tagName?.toLowerCase?.();
            const isTypingInField =
                tagName === "input" ||
                tagName === "textarea" ||
                target?.isContentEditable;
            if (isTypingInField) {
                return;
            }

            const key = event.key;

            // Prevent default behavior for number keys and specific keys
            if (
                /[0-9]/.test(key) ||
                ["Backspace", "Delete", "Enter", "Escape"].includes(key)
            ) {
                event.preventDefault();
            }

            if (/[0-9]/.test(key)) {
                handleUpdateAmount(parseInt(key));
            } else if (key === "Backspace" || key === "Delete") {
                handleUpdateClear();
            } else if (key === "Escape") {
                handleClearAll();
            } else if (key === "Enter") {
                // TODO: suspected to make repeated calls, not sure yet.
                // validateInput();
            }
        },
        [
            currentInput,
            activePaymentMethod,
            paymentAmounts,
            totalPaidInCompanyCurrency,
            finalOrderAmount,
            orderOnHouse,
            selectedButton,
            selectedCustomer,
        ]
    );

    useEffect(() => {
        // Add keyboard event listener
        window.addEventListener("keydown", handleKeyboardInput);

        return () => {
            window.removeEventListener("keydown", handleKeyboardInput);
        };
    }, [handleKeyboardInput]);

    // Enhanced calculator functions
    const handleUpdateAmount = (digit) => {
        let newValue = currentInput;

        // If current input is "0", replace it; otherwise append
        if (newValue === "0") {
            newValue = digit.toString();
        } else {
            newValue += digit.toString();
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== finalOrderAmount && activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const handleReplaceAmount = (amount) => {
        // Always use current discounted amount when called from order amount click
        const actualAmount =
            amount === finalOrderAmount ? finalOrderAmount : amount;
        const displayInActive =
            actualAmount === finalOrderAmount && activePaymentMethod
                ? convertCompanyToMethod(finalOrderAmount, activePaymentMethod)
                : actualAmount;
        const newValue = displayInActive.toString();
        setCurrentInput(newValue);

        // Skip parent update when setting amount via "Order amount" to prevent feedback loop
        const isOrderAmountClick = actualAmount === finalOrderAmount;
        updateActivePaymentAmount(displayInActive, isOrderAmountClick);

        // When setting via "Order amount" click, reset other methods and track this one
        if (actualAmount === finalOrderAmount && activePaymentMethod) {
            const entered = convertCompanyToMethod(finalOrderAmount, activePaymentMethod);
            setPaymentAmounts((prev) => {
                const next = { ...prev };
                paymentMethods.forEach((m) => {
                    next[m] = m === activePaymentMethod ? entered : 0;
                });
                return next;
            });
            setSetViaOrderAmountByMethod((prev) => {
                const next = { ...prev };
                paymentMethods.forEach((m) => {
                    next[m] = m === activePaymentMethod;
                });
                return next;
            });
        }
    };

    const handleUpdateClear = () => {
        let newValue = currentInput;

        if (newValue.length <= 1) {
            newValue = "0";
        } else {
            newValue = newValue.slice(0, -1);
        }

        setCurrentInput(newValue);
        const amount = parseFloat(newValue) || 0;
        updateActivePaymentAmount(amount, true); // Skip parent update for manual calculator input

        // Clear tracking flag if amount is different from order amount
        if (amount !== finalOrderAmount && activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const handleClearAll = () => {
        setCurrentInput("0");
        updateActivePaymentAmount(0, true); // Skip parent update for manual keypad input
        // Reset tracking flag for active method
        if (activePaymentMethod) {
            setSetViaOrderAmountByMethod((prev) => ({
                ...prev,
                [activePaymentMethod]: false,
            }));
        }
    };

    const updateActivePaymentAmount = (amount, skipParentUpdate = false) => {
        if (!activePaymentMethod) return;
        setPaymentAmounts((prev) => ({
            ...prev,
            [activePaymentMethod]: amount,
        }));
        // We intentionally avoid calling handlePropertyChange here; Widgets uses modeOfPayment
        // and only falls back to paymentData.amount when modeOfPayment is empty.
    };

    const handlePaymentTypeSwitch = (method) => {
        setActivePaymentMethod(method);
        const amount = Number(paymentAmounts?.[method]) || 0;
        const wasSetViaOrderAmount = !!setViaOrderAmountByMethod?.[method];
        const displayAmount = wasSetViaOrderAmount ? finalOrderAmount : amount;
        setCurrentInput(displayAmount === 0 ? "0" : displayAmount.toString());
        setErrorMessage(null);
    };

    const validateInput = () => {
        // Check if total payment covers the order amount
        if (
            totalPaidInCompanyCurrency < finalOrderAmount &&
            !orderOnHouse &&
            selectedButton !== "Deferred"
        ) {
            setErrorMessage(
                `Insufficient payment. Remaining: ₪${remainingAmount.toFixed(
                    2
                )}`
            );
            return false;
        }

        // Check if at least one payment method is used (unless special cases)
        if (!orderOnHouse && selectedButton !== "Deferred") {
            const anyAmount = paymentMethods.some(
                (m) => (Number(paymentAmounts[m]) || 0) > 0
            );
            if (!anyAmount) {
                setErrorMessage("Please enter payment amount");
                return false;
            }
        } else if (selectedButton && selectedButton === "Deferred") {
            if (!selectedCustomer) {
                setErrorMessage("Customer Not Selected");
                return false;
            }
        }

        // Prepare payment mode data for the payload
        const modeOfPayment = {};
        paymentMethods.forEach((m) => {
            const v = Number(paymentAmounts[m]) || 0;
            if (v > 0) modeOfPayment[m] = v;
        });

        // Update payment type for backward compatibility
        const nonZero = Object.keys(modeOfPayment);
        if (nonZero.length > 1) {
            setPaymentType("Mixed");
        } else if (nonZero.length === 1) {
            setPaymentType(nonZero[0]);
        }

        if (onConfirm) {
            // Prepare discount information to pass along with payment data
            const discountInfo = {
                discountType: discountType,
                fixedDiscountAmount:
                    discountType === "fixed" ? fixedDiscountAmount : 0,
                percentageDiscount:
                    discountType === "percentage" ? selectedDiscount : 0,
            };

            // Pass the modeOfPayment and discount info to the parent component
            onConfirm(modeOfPayment, discountInfo);
        }
        return true;
    };

    useEffect(() => {
        // Fetch the dollar rate from an API
        fetch("https://api.exchangerate-api.com/v4/latest/USD")
            .then((response) => response.json())
            .then((data) => {
                setDollarRate(data.rates.ILS);
            })
            .catch((error) => {
                console.error("Error fetching dollar rate:", error);
            });
    }, []);

    // Auto-update payment amounts when discount changes if they were set via "Order amount" click
    useEffect(() => {
        if (!activePaymentMethod) return;
        const anySet = Object.values(setViaOrderAmountByMethod || {}).some(Boolean);
        if (!anySet) return;

        // Find which method was set via order amount (should be at most one)
        const method = Object.keys(setViaOrderAmountByMethod || {}).find(
            (m) => setViaOrderAmountByMethod[m]
        );
        if (!method) return;

        const entered = convertCompanyToMethod(finalOrderAmount, method);
        setPaymentAmounts((prev) => ({
            ...prev,
            [method]: entered,
        }));
        if (activePaymentMethod === method) {
            setCurrentInput(entered.toString());
        }
    }, [
        finalOrderAmount,
        setViaOrderAmountByMethod,
        activePaymentMethod,
        exchangeRatesToCompany,
        methodCurrencyMap,
        companyCurrency,
    ]);

    useEffect(() => {
        if (isCreatingInvoice) {
            setInternalMessage("Creating invoice please wait");
        } else {
            setInternalMessage(null);
        }
    }, [isCreatingInvoice]);

    // Release click lock when no submission is in-flight
    useEffect(() => {
        if ((isMutating || 0) === 0 && !isCreatingInvoice && !isLoading) {
            confirmClickLockedRef.current = false;
        }
    }, [isMutating, isCreatingInvoice, isLoading]);

    const handleBackClicked = () => {
        if (backCallback) {
            backCallback();
        }
    };

    const getPaymentTypeButtonStyle = (method) => ({
        flex: 1,
        backgroundColor:
            activePaymentMethod === method ? "#e14954" : "#6E7187",
        color: "#FFFFFF",
        marginRight: "5px",
        marginLeft: "5px",
        border:
            activePaymentMethod === method ? "2px solid #000" : "1px solid #ccc",
    });

    const getButtonStyle1 = (type) => ({
        flex: 1,
        backgroundColor: selectedButton === type ? "#e14954" : "#6E7187",
        color: selectedButton === type ? "#FFFFFF" : "#FFFFFF",
        marginRight: "10px",
        border: selectedButton === type ? "1px solid #000" : "1px solid #ccc",
    });

    const handleButtonChange = (event, newButton) => {
        setSelectedButton(newButton);
    };

    useEffect(() => {
        if (selectedButton && selectedButton === "Deferred") {
            setOrderOnHouse(false);
        } else if (selectedButton && selectedButton === "OTH") {
            setOrderOnHouse(true);
        } else {
            setOrderOnHouse(false);
        }
    }, [selectedButton]);

    const changeCustomer = (customer) => {
        setChangeCustomerDialogOpen(true);
    };

    const onConfirmCustomerChanged = (customerName, address) => {
        refetchCustomers();
        refetchAddresses();
        setSelectedCustomer(customerName);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const onCustomerSelected = (customer, address) => {
        setSelectedCustomer(customer);
        setErrorMessage(null);
        setChangeCustomerDialogOpen(false);
    };

    const handleCustomerChangeClose = () => {
        setChangeCustomerDialogOpen(false);
    };

    // Handle fixed discount amount input change
    const handleFixedDiscountChange = (event) => {
        // Don't allow changes if this is a preset discount amount
        if (
            presetDiscountAmount != null &&
            presetDiscountAmount > 0
        ) {
            return;
        }

        const value =
            event.target.value === "" ? 0 : parseFloat(event.target.value) || 0;
        setFixedDiscountAmount(value);

        if (value > 0) {
            // Switch to fixed discount mode and clear percentage discount
            setDiscountType("fixed");
            if (onUpdateDiscount) {
                onUpdateDiscount(0); // Clear percentage discount
            }
        } else {
            // Switch back to percentage mode when fixed amount is 0 or empty
            setDiscountType("percentage");
            // Restore preset discount if it exists, otherwise keep current percentage
            if (presetDiscount != null && onUpdateDiscount) {
                onUpdateDiscount(presetDiscount); // Restore preset discount
            }
        }
    };

    // Handle percentage discount button click
    const handlePercentageDiscountClick = (percentage) => {
        // Don't allow percentage changes if there's a preset discount amount (when paying all)
        if (fixedDiscountAmount > 0) {
            return;
        }
        setDiscountPercentage(percentage);
    };

    return (
        <div
            className={classes.AddPaymentWidgetWrap}
            style={{
                height: "95vh",
                maxHeight: "95vh",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <div
                className={classes.AddPaymentWidget}
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                {/* Header - Fixed */}
                <div
                    className={classes.SmallTablePlaceholderWrapper}
                    style={{
                        flexShrink: 0,
                        zIndex: 1,
                    }}
                >
                    <SmallTablePlaceholder
                        backCallback={handleBackClicked}
                        tables={tables}
                        internalMessage={internalMessage}
                    />
                </div>

                {/* Scrollable Content Area */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        paddingBottom: "80px", // Extra space for footer
                        minHeight: 0,
                    }}
                >
                    {/* Top Row: Order Amount, Change, and Currency Rate */}
                    <div
                        style={{
                            display: "flex",
                            gap: "8px",
                            marginBottom: "15px",
                        }}
                    >
                        {/* Order Amount Section */}
                        <div
                            style={{
                                backgroundColor: "#f0f8ff",
                                border: "1px solid #b6d7ff",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: "pointer",
                                minHeight: "50px",
                            }}
                            onClick={() =>
                                handleReplaceAmount(finalOrderAmount)
                            }
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#1e3a8a",
                                }}
                            >
                                Order Amount:
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1d4ed8",
                                }}
                            >
                                ₪{finalOrderAmount}
                            </span>
                        </div>

                        {/* Change Section */}
                        <div
                            style={{
                                backgroundColor: "#e8f5e8",
                                border: "1px solid #c3e6c3",
                                borderRadius: "6px",
                                padding: "10px 12px",
                                flex: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                minHeight: "50px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    color: "#2d5a2d",
                                }}
                            >
                                Change:
                            </span>
                            <span
                                style={{
                                    fontSize: "18px",
                                    fontWeight: "bold",
                                    color: "#1e7e34",
                                }}
                            >
                                ₪{changeAmount.toFixed(2)}
                            </span>
                        </div>

                        {/* Currency Rate Mini Card */}
                        <div
                            style={{
                                backgroundColor: "#fff8e1",
                                border: "1px solid #ffcc02",
                                borderRadius: "6px",
                                padding: "8px 10px",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "50px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    color: "#e65100",
                                    marginBottom: "2px",
                                }}
                            >
                                USD
                            </span>
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#ef6c00",
                                }}
                            >
                                ₪{dollarRate?.toFixed(2) || "-.--"}
                            </span>
                        </div>
                    </div>

                    {/* Payment Summary (Dynamic) */}
                    <div
                        style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e9ecef",
                            borderRadius: "6px",
                            padding: "12px",
                            marginBottom: "15px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "10px",
                                color: "#333",
                                borderBottom: "1px solid #dee2e6",
                                paddingBottom: "6px",
                                textAlign: "center",
                            }}
                        >
                            Payment Summary
                        </div>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                                alignItems: "stretch",
                                gap: "10px",
                            }}
                        >
                            {paymentMethods.map((method) => (
                                <div
                                    key={method}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        minWidth: "120px",
                                        flex: "1 1 120px",
                                    }}
                                >
                                    <span
                                        style={{
                                            color: "#666",
                                            fontSize: "12px",
                                            marginBottom: "3px",
                                            textAlign: "center",
                                        }}
                                    >
                                        {method}
                                    </span>
                                    <span
                                        style={{
                                            fontWeight: "bold",
                                            color: "#333",
                                            fontSize: "16px",
                                        }}
                                    >
                                        {getCurrencySymbol(
                                            methodCurrencyMap[method] || companyCurrency
                                        )}
                                        {Number(paymentAmounts[method] || 0).toFixed(2)}
                                    </span>
                                    {(methodCurrencyMap[method] || companyCurrency).toUpperCase() !==
                                        companyCurrency.toUpperCase() ? (
                                        <span
                                            style={{
                                                color: "#6E7187",
                                                fontSize: "12px",
                                                marginTop: "2px",
                                            }}
                                        >
                                            ≈ {getCurrencySymbol(companyCurrency)}
                                            {Number(
                                                paymentAmountsInCompanyCurrency[method] || 0
                                            ).toFixed(2)}
                                        </span>
                                    ) : null}
                                </div>
                            ))}
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: "120px",
                                    flex: "1 1 120px",
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Total
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color: "#2c3e50",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{totalPaidInCompanyCurrency.toFixed(2)}
                                </span>
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    minWidth: "120px",
                                    flex: "1 1 120px",
                                }}
                            >
                                <span
                                    style={{
                                        color: "#666",
                                        fontSize: "12px",
                                        marginBottom: "3px",
                                    }}
                                >
                                    Remaining
                                </span>
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        color:
                                            remainingAmount > 0
                                                ? "#e74c3c"
                                                : "#28a745",
                                        fontSize: "16px",
                                    }}
                                >
                                    ₪{remainingAmount.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={classes.MainContainer}>
                        <div>
                            {/* Quick Amount Buttons */}
                            <div className={classes.Tips}>
                                <div className="Tips__Amounts">
                                    {[10, 20, 50, 100, 200].map((entry) => {
                                        return (
                                            <div
                                                key={entry}
                                                onClick={() =>
                                                    handleReplaceAmount(entry)
                                                }
                                                className={classNames(
                                                    "Number",
                                                    {
                                                        IsSelected:
                                                            selectedTip ===
                                                            entry,
                                                    }
                                                )}
                                            >
                                                {entry}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Calculator Keypad */}
                            <div className={classes.Keys}>
                                {[9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((entry) => {
                                    return (
                                        <div
                                            key={entry}
                                            className={classNames("Number")}
                                            onClick={() =>
                                                handleUpdateAmount(entry)
                                            }
                                        >
                                            {entry}
                                        </div>
                                    );
                                })}
                                <div className="Clear" onClick={handleClearAll}>
                                    C
                                </div>
                                <div
                                    className="Delete"
                                    onClick={handleUpdateClear}
                                >
                                    <img src={clear} alt="delete" />
                                </div>
                            </div>
                        </div>

                        <div className={classes.PaymentType}>
                            <div className={classes.VerticalContainer}>
                                {errorMessage && (
                                    <div
                                        style={{
                                            color: "red",
                                            marginBottom: "10px",
                                        }}
                                    >
                                        {errorMessage}
                                    </div>
                                )}

                                {/* Payment Method Selection */}
                                <div style={{ marginBottom: "15px" }}>
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            marginBottom: "8px",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Active Payment Method:
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            width: "100%",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {paymentMethods.map((method) => (
                                            <Button
                                                key={method}
                                                onClick={() =>
                                                    handlePaymentTypeSwitch(method)
                                                }
                                                style={getPaymentTypeButtonStyle(
                                                    method
                                                )}
                                            >
                                                {method} (₪
                                                {Number(
                                                    paymentAmounts[method] || 0
                                                ).toFixed(2)}
                                                )
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                {/* Current Input Display */}
                                <div style={{ marginBottom: "15px" }}>
                                    <TextField
                                        label={`${activePaymentMethod || "Payment"} Amount`}
                                        value={currentInput}
                                        variant="outlined"
                                        // fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    {getCurrencySymbol(
                                                        methodCurrencyMap[activePaymentMethod] ||
                                                            companyCurrency
                                                    )}
                                                </InputAdornment>
                                            ),
                                            readOnly: true,
                                            style: {
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                            },
                                        }}
                                        style={{ backgroundColor: "#f5f5f5" }}
                                    />
                                </div>

                                {/* Discount Section */}
                                <div>
                                    {userCanGiveDiscount() && (
                                        <div className={classes.Discount}>
                                            {/* Fixed Amount Discount Input */}
                                            <div
                                                style={{ marginBottom: "15px" }}
                                            >
                                                <TextField
                                                    label="Fixed Discount Amount"
                                                    type="number"
                                                    value={
                                                        fixedDiscountAmount ||
                                                        ""
                                                    }
                                                    onChange={
                                                        handleFixedDiscountChange
                                                    }
                                                    variant="outlined"
                                                    // size="small"
                                                    disabled={
                                                        invoice.discount_amount >
                                                        0
                                                    }
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                ₪
                                                            </InputAdornment>
                                                        ),
                                                        style: {
                                                            fontSize: "18px",
                                                            fontWeight: "bold",
                                                        },
                                                    }}
                                                    inputProps={{
                                                        min: 0,
                                                        step: 0.01,
                                                    }}
                                                    style={{
                                                        backgroundColor:
                                                            presetDiscountAmount !=
                                                                null &&
                                                            presetDiscountAmount >
                                                                0 &&
                                                            payingAll
                                                                ? "#f0f0f0"
                                                                : "#f5f5f5",
                                                        // width: "100%",
                                                        // backgroundColor: "white"
                                                    }}
                                                    helperText={
                                                        presetDiscountAmount !=
                                                            null &&
                                                        presetDiscountAmount >
                                                            0 &&
                                                        payingAll
                                                            ? "Preset discount amount from table order"
                                                            : !payingAll
                                                            ? "Enter amount to discount selected items"
                                                            : discountType ===
                                                                  "fixed" &&
                                                              fixedDiscountAmount >
                                                                  0
                                                            ? "Fixed discount applied (overrides preset discount)"
                                                            : "Enter amount to use fixed discount"
                                                    }
                                                />
                                            </div>

                                            {/* Percentage Discount Buttons */}
                                            <div
                                                className={`Discount__Amounts ${
                                                    fixedDiscountAmount > 0
                                                        ? "Disabled"
                                                        : ""
                                                }`}
                                            >
                                                {[0, 5, 10, 15, 20].map(
                                                    (entry) => {
                                                        return (
                                                            <div
                                                                key={entry}
                                                                onClick={() =>
                                                                    fixedDiscountAmount >
                                                                    0
                                                                        ? console.log(
                                                                              "Discount mode disabled or preset amount exists"
                                                                          )
                                                                        : handlePercentageDiscountClick(
                                                                              entry
                                                                          )
                                                                }
                                                                className={classNames(
                                                                    "Number",
                                                                    "DiscountButton",
                                                                    {
                                                                        IsSelected:
                                                                            discountPercentage ===
                                                                            entry,
                                                                    }
                                                                )}
                                                                style={{
                                                                    opacity:
                                                                        fixedDiscountAmount >
                                                                        0
                                                                            ? 0.5
                                                                            : 1,
                                                                    cursor:
                                                                        fixedDiscountAmount >
                                                                        0
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                }}
                                                            >
                                                                {entry}%
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Special Payment Options */}
                                <div
                                    style={{ width: "100%", marginTop: "15px" }}
                                >
                                    <ToggleButtonGroup
                                        style={{
                                            width: "100%",
                                            display: "flex",
                                        }}
                                        value={selectedButton}
                                        exclusive
                                        onChange={handleButtonChange}
                                        aria-label="payment options"
                                    >
                                        <ToggleButton
                                            value="OTH"
                                            aria-label="OTH"
                                            style={getButtonStyle1("OTH")}
                                        >
                                            OTH
                                        </ToggleButton>
                                        <ToggleButton
                                            value="Deferred"
                                            aria-label="Deferred"
                                            style={getButtonStyle1("Deferred")}
                                        >
                                            Deferred
                                        </ToggleButton>
                                        {selectedButton &&
                                            selectedButton === "Deferred" && (
                                                <div>
                                                    <IconButton
                                                        className={
                                                            classes.Table_edit_customer
                                                        }
                                                        onClick={() =>
                                                            changeCustomer(
                                                                selectedCustomer
                                                            )
                                                        }
                                                    >
                                                        <PersonAddAlt1
                                                            fontSize="x-small"
                                                            color="white"
                                                        />
                                                    </IconButton>
                                                </div>
                                            )}
                                    </ToggleButtonGroup>
                                </div>

                                {selectedButton && (
                                    <div
                                        style={{
                                            marginTop: "10px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        {selectedButton === "OTH" && (
                                            <div>Order on House Selected</div>
                                        )}
                                        {selectedButton === "Deferred" && (
                                            <div>
                                                {selectedCustomer ? (
                                                    <div>
                                                        Payment for{" "}
                                                        {selectedCustomer}
                                                    </div>
                                                ) : (
                                                    <div>Select Customer</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Fixed Footer - Always Visible at Bottom */}

                <button
                    className="Btn--Primary"
                    onClick={() => {
                        if (confirmClickLockedRef.current) return;
                        confirmClickLockedRef.current = true;
                        const ok = validateInput();
                        if (!ok) {
                            // Unlock immediately if validation failed
                            confirmClickLockedRef.current = false;
                        }
                    }}
                    disabled={isMutating > 0 || isCreatingInvoice || isLoading}
                >
                    {isMutating > 0 || isCreatingInvoice || isLoading ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress
                                size={20}
                                style={{ marginRight: "10px", color: "white" }}
                            />
                            Confirming payment...
                        </div>
                    ) : (
                        "Confirm Payment & Print receipt"
                    )}
                </button>

                <CustomerDialog
                    dialogOpen={changeCustomerDialogOpen}
                    onCancel={handleCustomerChangeClose}
                    onConfirm={onConfirmCustomerChanged}
                    selectedCustomer={selectedCustomer}
                    customerSelected={onCustomerSelected}
                ></CustomerDialog>
            </div>
        </div>
    );
};

export default AddPaymentWidget;
